import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getV1CompaniesCompanyIdOrdersOrderIdJobsInvoicing,
  GetV1CompaniesCompanyIdOrdersOrderIdJobsInvoicingData,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { noop } from 'lodash-es';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { JobInvoicing } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { APPROVALS_JOB_STATES } from '~pages/Approvals/Files/constants';
import FilesPageContext from '~pages/Approvals/Files/FilesPageContext';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import { makeJobsQueryContextKey } from '~pages/Approvals/Files/utils';
import { approvalFilterToBoolean } from '~utils/filters/approval-filter-utils';

type JobsQueryContextType = {
  clearQueryData: () => void;
  data?: JobInvoicing[];
  fetch: () => void;
  isError?: boolean;
  order: Order;
  updateJobInQueryData: (job: JobInvoicing) => void;
};

const JobsQueryContext = createContext<JobsQueryContextType>({
  clearQueryData: noop,
  fetch: noop,
  order: {} as Order,
  updateJobInQueryData: noop,
});

interface JobsQueryContextProviderProps {
  order: Order;
  companyId: string;
  extraJobsQuery?: GetV1CompaniesCompanyIdOrdersOrderIdJobsInvoicingData['query'];
  shouldFetchOnMount?: boolean;
}

function JobsQueryContextProvider({
  children,
  order,
  companyId,
  extraJobsQuery,
  shouldFetchOnMount = false,
}: React.PropsWithChildren<JobsQueryContextProviderProps>) {
  const { reportJobsLoaded } = useContext(FilesPageContext);
  const { filters: filesQueryFilters } = useContext(FilesQueryContext);

  const queryKey = makeJobsQueryContextKey(order.id);
  const queryClient = useQueryClient();

  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    if (shouldFetchOnMount) {
      setShouldFetch(true);
    }
  }, [shouldFetchOnMount]);

  const query = useQuery({
    queryKey: [queryKey],
    queryFn: shouldFetch
      ? async ({ signal }) => {
          const {
            customerAccountIds,
            driverIds,
            dropoffSiteIds,
            endDate,
            equipmentOwnerCompanyIds,
            foremanIds,
            jobApprovalStatus,
            jobStates,
            loadApprovalStatus,
            payablesStates,
            pickupSiteIds,
            projectExternalIds,
            projectIds,
            receivablesStates,
            searchQuery,
            siteTypes,
            startDate,
            vendorAccountIds,
          } = filesQueryFilters;

          // FIXME @todo To be properly paginated in a follow-up PR.
          const query: GetV1CompaniesCompanyIdOrdersOrderIdJobsInvoicingData['query'] = {
            'page[limit]': MAX_API_PAGE_SIZE,
            // Matches the order-cards query: an order can only match on an assigned
            // job, so the rows have to be narrowed the same way or a card whose sole
            // qualifying job was unassigned would expand to zero rows.
            'filter[assigned]': true,
            'filter[billing_approved]': approvalFilterToBoolean(jobApprovalStatus),
            'filter[customer_account_ids]': customerAccountIds?.length
              ? customerAccountIds
              : undefined,
            'filter[driver_ids]': driverIds?.length ? driverIds : undefined,
            'filter[dropoff_site_ids]': dropoffSiteIds?.length
              ? dropoffSiteIds
              : undefined,
            'filter[external_ids]': projectExternalIds?.length
              ? projectExternalIds
              : undefined,
            'filter[end_date]': endDate
              ? dayjs.tz(endDate).endOf('day').toISOString()
              : undefined,
            'filter[foreman_ids]': foremanIds?.length ? foremanIds : undefined,
            'filter[load][billing_approved]': approvalFilterToBoolean(loadApprovalStatus),
            'filter[pickup_site_ids]': pickupSiteIds?.length ? pickupSiteIds : undefined,
            'filter[project_ids]': projectIds?.length ? projectIds : undefined,
            'filter[start_date]': startDate
              ? dayjs.tz(startDate).startOf('day').toISOString()
              : undefined,
            'filter[states]': jobStates?.length ? jobStates : APPROVALS_JOB_STATES,
            'filter[site_types]': siteTypes?.length ? siteTypes : undefined,
            'filter[vendor_account_ids]': vendorAccountIds?.length
              ? vendorAccountIds
              : undefined,
            'filter[equipment][company_ids]': equipmentOwnerCompanyIds?.length
              ? equipmentOwnerCompanyIds
              : undefined,
            'filter[payable_invoice][states]': payablesStates?.length
              ? payablesStates
              : undefined,
            'filter[receivable_invoice][states]': receivablesStates?.length
              ? receivablesStates
              : undefined,
            'search[datagrid]': searchQuery || undefined,
            ...extraJobsQuery,
          };

          const response = await getV1CompaniesCompanyIdOrdersOrderIdJobsInvoicing({
            path: { 'company-id': companyId, 'order-id': order.id },
            query,
            signal,
          });

          const jobs = response.data.data.map((job) => JobInvoicing.parse(job));

          return jobs;
        }
      : skipToken,
  });

  const fetch = useCallback(() => {
    setShouldFetch(true);
  }, []);

  const updateJobInQueryData = useCallback(
    (job: JobInvoicing) => {
      queryClient.setQueryData([queryKey], (oldJobs: JobInvoicing[]) => {
        const jobs = oldJobs ?? [];
        const oldJobIndex = jobs.findIndex((j) => j.id === job.id);

        if (oldJobIndex >= 0) {
          const newJobs = [...jobs];
          newJobs.splice(oldJobIndex, 1, job);

          return newJobs;
        }

        return jobs;
      });
    },
    [queryKey],
  );

  const clearQueryData = useCallback(() => {
    queryClient.removeQueries({ queryKey: [queryKey] });
  }, [queryKey]);

  useEffect(() => {
    reportJobsLoaded(query.data ?? []);
  }, [query.data, reportJobsLoaded]);

  return (
    <JobsQueryContext.Provider
      value={{
        clearQueryData,
        data: query.data,
        fetch,
        isError: query.isError,
        order,
        updateJobInQueryData,
      }}
    >
      {children}
    </JobsQueryContext.Provider>
  );
}

export default JobsQueryContext;
export { JobsQueryContextProvider };
